import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { useCallback, useEffect, useRef, useState } from 'react';

import { usePrevious } from '@/hooks/usePrevious';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { trackAdUnitClick, trackAdUnitVisible } from '@/redux/modules/analytics';
import { getCatalogAd } from '@/redux/modules/catalog/ad/catalogAd.selectors';
import { AmpProduct, useTrackAmpClicked, useTrackAmpSeen } from '@/utils/amplitude/analytics';
import { getCategoryIdsFromQueryParams, getKeywordFromQueryParams } from '@/utils/queryParams';
import { getCategoryIdsFromPathname } from '@/utils/urls';

import CatalogAd from './CatalogAd';
import { ConsignmentAd } from './ConsignmentAd';

type Props = {
    className?: string;
    isLandingPage?: boolean;
    pageName?: string;
    pathname: string;
    queryParams: any;
};

export const AdContainer = ({ className = '', isLandingPage, pageName, pathname, queryParams }: Props) => {
    const dispatch = useAppDispatch();
    const targetRef = useRef<HTMLDivElement>(null);

    const adRef = useRef<HTMLDivElement>(null);
    const { isVisible } = useVisibilitySensor(adRef, {
        intervalCheck: false,
        resizeCheck: true,
        scrollCheck: true,
    });

    const trackAmpClicked = useTrackAmpClicked();
    const trackAmpSeen = useTrackAmpSeen();

    const keywords = getKeywordFromQueryParams(queryParams);
    const browseIdsCsv = useAppSelector((state) =>
        isLandingPage && state.browsePath.baseCategoryIds.length ? state.browsePath.baseCategoryIds.join(',') : ''
    );
    let categoryIds = isLandingPage
        ? browseIdsCsv || getCategoryIdsFromPathname(pathname)
        : getCategoryIdsFromQueryParams(queryParams);
    categoryIds = pageName === 'calendar' ? '' : categoryIds;

    const adUnit = useAppSelector((state) => getCatalogAd(state, { categoryIds, keywords: `${keywords}`, pageName }));
    const previousIsVisible = usePrevious(isVisible);
    const [visibilityMessageSent, setVisibilityMessageSent] = useState(false);
    const { adUnitId, catalogId, consignment, sellerId } = adUnit;

    useEffect(() => {
        if (!previousIsVisible && isVisible && !visibilityMessageSent && Boolean(adUnitId)) {
            dispatch(
                trackAdUnitVisible({
                    adUnitId,
                    page: pageName,
                })
            ).then(() => {
                setVisibilityMessageSent(true);
            });

            // Track AMP Seen event
            trackAmpSeen({ ampProduct: AmpProduct.CategoryPageBanner, catalogId, houseId: sellerId });
        }
    }, [
        dispatch,
        isVisible,
        adUnitId,
        previousIsVisible,
        catalogId,
        consignment,
        pageName,
        sellerId,
        visibilityMessageSent,
        trackAmpSeen,
    ]);

    useEffect(() => {
        setVisibilityMessageSent(false);
    }, [adUnitId]);

    const trackClick = useCallback(() => {
        dispatch(
            trackAdUnitClick({
                adUnitId,
                consignment,
                page: pageName || '',
            })
        );

        // Track AMP Clicked event
        trackAmpClicked({ ampProduct: AmpProduct.CategoryPageBanner, catalogId, houseId: sellerId });
    }, [dispatch, adUnitId, catalogId, consignment, pageName, sellerId, trackAmpClicked]);

    const [componentOffset, setComponentOffset] = useState(0);
    const [windowHeight, setWindowHeight] = useState(0);
    const [componentHeight, setComponentHeight] = useState(0);
    const [scrolled, setScrolled] = useState(false);

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const newWindowHeight = document?.documentElement?.scrollHeight;
        if (windowHeight !== newWindowHeight) {
            setWindowHeight(newWindowHeight || 1500);
        }
        const newComponentHeight = targetRef?.current?.clientHeight;
        if (componentHeight !== newComponentHeight && newComponentHeight) {
            setComponentHeight(newComponentHeight);
        }
    }

    const noAd = !adUnit || !adUnit.adUnitId;

    useScrollPosition(
        () => {
            if (!scrolled) {
                setScrolled(true);
            }
            setComponentOffset(targetRef?.current?.getBoundingClientRect()?.top || 100);
        },
        [scrolled],
        noAd ? undefined : targetRef,
        false,
        10
    );

    if (noAd) {
        return null;
    }

    const componentParent = targetRef.current?.parentElement;
    const componentParentBottom = componentParent?.getBoundingClientRect()?.bottom;
    const componentBottomGap = componentParentBottom - componentHeight;
    const componentParentChildrenTotalHeight = Array.from(componentParent?.children || []).reduce((acc, child) => {
        const element = child as HTMLElement;
        const computedStyle = window.getComputedStyle(element);
        const marginTop = parseInt(computedStyle.marginTop, 10);
        const marginBottom = parseInt(computedStyle.marginBottom, 10);
        const height = element.getBoundingClientRect().height + marginTop + marginBottom;
        return acc + height;
    }, 0);

    const tooSmallToMove = componentParentChildrenTotalHeight >= componentParent?.clientHeight;
    const stickBottom = !tooSmallToMove && Boolean(componentBottomGap <= 0);
    const stick = !tooSmallToMove && Boolean(componentOffset <= 0);

    const Ad = consignment ? ConsignmentAd : CatalogAd;

    return (
        <div
            className="flex w-full items-center justify-center"
            data-testid="ad-container"
            ref={targetRef}
        >
            <Ad
                adUnit={{ ...adUnit, sellerId }}
                className={className}
                scrolled={scrolled}
                stick={stick}
                stickBottom={stickBottom}
                trackClick={trackClick}
            >
                <div
                    className="h-[1px] w-full bg-transparent"
                    ref={adRef}
                />
            </Ad>
        </div>
    );
};
